import { useState } from 'react';
import type { SensitivityRow } from '../types';
import { formatNumber, formatSignedDays } from '../utils/format';

interface Props {
  rows: SensitivityRow[];
  q10Values: number[];
  onQ10ValuesChange: (values: number[]) => void;
}

const PRESETS: { label: string; values: number[] }[] = [
  { label: '1.8 – 2.5', values: [1.8, 2.0, 2.2, 2.5] },
  { label: '1.5 – 3.0', values: [1.5, 2.0, 2.5, 3.0] },
  { label: '1.0 – 2.0', values: [1.0, 1.25, 1.5, 1.75, 2.0] },
];

export function Q10Sensitivity({ rows, q10Values, onQ10ValuesChange }: Props) {
  const [draft, setDraft] = useState(q10Values.join(', '));
  const [parseError, setParseError] = useState<string | null>(null);

  if (rows.length === 0) {
    return <p className="empty-state">Sensitivity appears once a valid scenario is entered.</p>;
  }

  const maxAAT = Math.max(...rows.map((r) => (Number.isFinite(r.aatDays) ? r.aatDays : 0)));

  const applyDraft = (text: string) => {
    setDraft(text);
    const values = text
      .split(/[,\s]+/)
      .filter((s) => s.trim() !== '')
      .map(Number);
    if (values.length === 0 || values.some((v) => !Number.isFinite(v) || v <= 0)) {
      setParseError('Enter positive numbers separated by commas.');
      return;
    }
    setParseError(null);
    onQ10ValuesChange(values);
  };

  const applyPreset = (values: number[]) => {
    setDraft(values.join(', '));
    setParseError(null);
    onQ10ValuesChange(values);
  };

  return (
    <div className="explorer">
      <p className="explorer-lede">
        Explore how the assumed Q10 affects the calculated aging duration. Your entered value is marked; the others are
        shown for comparison only.
      </p>

      <div className="explorer-controls">
        <label htmlFor="q10-range">Q10 values to compare</label>
        <input
          id="q10-range"
          className={`input ${parseError ? 'error' : ''}`}
          value={draft}
          onChange={(e) => applyDraft(e.target.value)}
          aria-describedby="q10-range-help"
        />
        <div className="preset-row">
          {PRESETS.map((preset) => (
            <button key={preset.label} type="button" className="chip" onClick={() => applyPreset(preset.values)}>
              {preset.label}
            </button>
          ))}
        </div>
        <p className="field-help" id="q10-range-help">
          {parseError ?? 'Comma-separated. Your own Q10 is always included.'}
        </p>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Q10 assumption</th>
              <th scope="col">AAF</th>
              <th scope="col">Calculated AAT</th>
              <th scope="col">vs. your Q10</th>
              <th scope="col" className="col-viz">
                Relative duration
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.q10} className={row.isBaseline ? 'is-baseline' : ''}>
                <th scope="row" className="num">
                  {row.q10}
                  {row.isBaseline && <span className="baseline-flag">yours</span>}
                </th>
                <td className="num">{formatNumber(row.aaf, 4)}</td>
                <td className="num">{formatNumber(row.aatDays, 3)} d</td>
                <td className="num delta">{formatSignedDays(row.deltaDaysVsBaseline)}</td>
                <td className="col-viz">
                  <span
                    className="bar"
                    style={{ width: `${maxAAT > 0 ? Math.max(2, (row.aatDays / maxAAT) * 100) : 0}%` }}
                    aria-hidden="true"
                  />
                  <span className="sr-only">{formatNumber(row.aatDays, 1)} days</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="explorer-note">
        The spread across this table is produced entirely by one assumed number. Nothing here indicates which value is
        correct for your materials — that judgement, and its documented basis, stay with you.
      </p>
    </div>
  );
}
